import React from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDashContext } from "../context/DashboardContext";

import SideNavigation from "./sideNavigation";
import UnsavedChangesDialog from "./shared/UnsavedChangesDialog";

const CustomAppBar = ({ tenant, showLoginButton }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { hasUnsavedChanges, setHasUnsavedChanges } = useDashContext();

  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = React.useState(false);

  const displayUser = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, md: 1.5 } }}>
      <Avatar
        src={user?.avatar_url || undefined}
        sx={{
          backgroundColor: "grey.200",
          color: "text.primary",
          fontWeight: 600,
          width: { xs: 32, md: 40 },
          height: { xs: 32, md: 40 },
          fontSize: { xs: 12, md: 15 },
        }}
      >
        {!user?.avatar_url && [user?.first_name, user?.last_name]
          .filter(Boolean)
          .map((n) => n.charAt(0).toUpperCase())
          .join("")}
      </Avatar>

      <Box sx={{ display: "flex", flexDirection: "column", overflow: "hidden", maxWidth: { xs: 88, md: "none" } }}>
        <Typography
          variant="body1"
          noWrap
          sx={{ fontWeight: 400, lineHeight: "1.1", color: "text.primary" }}
        >
          <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
            {user?.first_name} {user?.last_name}
          </Box>
          <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
            {user?.first_name} {user?.last_name?.charAt(0)}.
          </Box>
        </Typography>
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontWeight: 400,
            lineHeight: "1.1",
            color: "text.secondary",
            display: user?.role === "client" ? "none" : "block",
          }}
        >
          {user?.role?.charAt(0)?.toUpperCase() + user?.role?.slice(1)}
        </Typography>
      </Box>

      <IconButton size="small" onClick={(e) => setAnchorElUser(e.currentTarget)}>
        <KeyboardArrowDownIcon />
      </IconButton>

      <Menu
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        keepMounted
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(anchorElUser)}
        onClose={() => setAnchorElUser(null)}
        slotProps={{ paper: { sx: { width: 200, mt: 3 } } }}
      >
        <MenuItem
          onClick={() => {
            setAnchorElUser(null);
            if (hasUnsavedChanges) {
              setShowUnsavedDialog(true);
              return;
            }
            navigate("/dashboard/profile");
          }}
        >
          <Typography sx={{ textAlign: "center" }}>Profile</Typography>
        </MenuItem>
        <MenuItem onClick={() => logout()}>
          <Typography sx={{ textAlign: "center" }}>Logout</Typography>
        </MenuItem>
      </Menu>

      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onStay={() => setShowUnsavedDialog(false)}
        onDiscard={() => {
          setShowUnsavedDialog(false);
          setHasUnsavedChanges(false);
          navigate("/dashboard/profile");
        }}
      />
    </Box>
  );

  return (
    <>
      {/* Mobile drawer — SideNavigation is now self-contained and role-aware */}
      <Drawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        anchor="left"
        slotProps={{ paper: { sx: { width: "50%" } } }}
      >
        <SideNavigation onNavigate={() => setIsDrawerOpen(false)} />
      </Drawer>

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: "transparent",
          color: "black",
          borderBottom: "0.1px solid",
          borderColor: "customDivider",
        }}
      >
        <Toolbar>
          {user && (
            <MenuIcon
              sx={{ display: { xs: "flex", md: "none" }, mr: 1 }}
              onTouchEnd={(e) => { e.preventDefault(); setIsDrawerOpen(true); }}
              onClick={() => setIsDrawerOpen(true)}
            />
          )}

          <Typography
            variant="h6"
            noWrap
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              display: { xs: "block", md: "none" },
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {tenant?.name}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              display: { xs: "none", md: "block" },
            }}
          >
            {tenant?.name}
          </Typography>

          {user
            ? displayUser()
            : showLoginButton && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/login")}
                >
                  Login
                </Button>
              )}
        </Toolbar>
      </AppBar>
    </>
  );
};

export default CustomAppBar;
